import type {
  LifeDisabilityCoverageConfig,
  EarningsBase,
  RoundingRule,
  CoverageAmountType,
} from "../schema";

/**
 * Employee earnings data structure for coverage calculations
 */
export interface EmployeeEarnings {
  baseSalary: number;
  annualBonus?: number;
  commission?: number;
  otherCompensation?: number;
  w2Earnings?: number;
}

/**
 * Coverage calculation result
 */
export interface CoverageCalculationResult {
  calculatedAmount: number; // Raw calculated amount before rounding
  roundedAmount: number; // Amount after applying rounding rules
  earningsUsed: number; // The earnings amount used in calculation
  exceedsMaximum: boolean; // Whether calculated amount exceeds max
  belowMinimum: boolean; // Whether calculated amount is below min
  finalCoverageAmount: number; // Final amount after min/max constraints
  requiresEOI: boolean; // Whether EOI is required
  guaranteedIssueAmount: number; // The GI limit applied
  amountRequiringEOI: number; // Amount over GI that requires EOI
}

/**
 * Get earnings amount based on configured earnings base
 */
export function getEarningsForBase(
  earnings: EmployeeEarnings,
  earningsBase: EarningsBase | string
): number {
  switch (earningsBase) {
    case "base_salary":
      return earnings.baseSalary;

    case "base_plus_bonus":
      return earnings.baseSalary + (earnings.annualBonus || 0);

    case "base_plus_commission":
      return earnings.baseSalary + (earnings.commission || 0);

    case "total_compensation":
      return (
        earnings.baseSalary +
        (earnings.annualBonus || 0) +
        (earnings.commission || 0) +
        (earnings.otherCompensation || 0)
      );

    case "w2_earnings":
      return earnings.w2Earnings || earnings.baseSalary;

    default:
      return earnings.baseSalary;
  }
}

/**
 * Apply rounding rule to coverage amount
 */
export function applyRoundingRule(
  amount: number,
  roundingRule: RoundingRule | string
): number {
  switch (roundingRule) {
    case "none":
      return amount;

    case "nearest_thousand":
      return Math.round(amount / 1000) * 1000;

    case "nearest_ten_thousand":
      return Math.round(amount / 10000) * 10000;

    case "round_up_thousand":
      return Math.ceil(amount / 1000) * 1000;

    case "round_down_thousand":
      return Math.floor(amount / 1000) * 1000;

    case "round_up_ten_thousand":
      return Math.ceil(amount / 10000) * 10000;

    case "round_down_ten_thousand":
      return Math.floor(amount / 10000) * 10000;

    case "nearest_hundred":
      return Math.round(amount / 100) * 100;

    default:
      return amount;
  }
}

/**
 * Calculate coverage amount based on config and employee earnings
 */
export function calculateCoverageAmount(
  config: LifeDisabilityCoverageConfig,
  earnings: EmployeeEarnings,
  eligibilityWindow: "newly_eligible" | "open_enrollment" | "qualifying_event" | "late_entrant" = "newly_eligible"
): CoverageCalculationResult {
  let calculatedAmount = 0;
  let earningsUsed = 0;

  // Step 1: Calculate base coverage amount
  if (config.coverageAmountType === "flat_amount") {
    calculatedAmount = parseFloat(config.flatCoverageAmount?.toString() || "0");
  } else if (config.coverageAmountType === "earnings_multiple") {
    earningsUsed = getEarningsForBase(earnings, config.earningsBase || "base_salary");
    const multiple = parseFloat(config.earningsMultiple?.toString() || "0");
    calculatedAmount = earningsUsed * multiple;
  } else if (config.coverageAmountType === "earnings_percentage") {
    earningsUsed = getEarningsForBase(earnings, config.earningsBase || "base_salary");
    const percentage = parseFloat(config.earningsPercentage?.toString() || "0");
    calculatedAmount = earningsUsed * (percentage / 100);
  }

  // Step 2: Apply rounding rule
  const roundedAmount = applyRoundingRule(
    calculatedAmount,
    config.roundingRule || "none"
  );

  // Step 3: Apply min/max constraints
  const minimumCoverage = parseFloat(config.minimumCoverage?.toString() || "0");
  const maximumCoverage = parseFloat(config.maximumCoverage?.toString() || "Infinity");

  const exceedsMaximum = roundedAmount > maximumCoverage;
  const belowMinimum = roundedAmount < minimumCoverage;

  let finalCoverageAmount = roundedAmount;
  if (exceedsMaximum) {
    finalCoverageAmount = maximumCoverage;
  } else if (belowMinimum && minimumCoverage > 0) {
    finalCoverageAmount = minimumCoverage;
  }

  // Step 4: Determine EOI requirement
  let guaranteedIssueAmount = 0;

  // Select appropriate GI limit based on eligibility window
  switch (eligibilityWindow) {
    case "newly_eligible":
      guaranteedIssueAmount = parseFloat(
        config.guaranteedIssueNewHire?.toString() ||
        config.guaranteedIssueAmount?.toString() ||
        "0"
      );
      break;

    case "open_enrollment":
      guaranteedIssueAmount = parseFloat(
        config.guaranteedIssueOpenEnrollment?.toString() ||
        config.guaranteedIssueAmount?.toString() ||
        "0"
      );
      break;

    case "qualifying_event":
    case "late_entrant":
    default:
      guaranteedIssueAmount = parseFloat(
        config.guaranteedIssueAmount?.toString() || "0"
      );
      break;
  }

  const requiresEOI = finalCoverageAmount > guaranteedIssueAmount && config.eoiRequired === true;
  const amountRequiringEOI = requiresEOI
    ? finalCoverageAmount - guaranteedIssueAmount
    : 0;

  return {
    calculatedAmount,
    roundedAmount,
    earningsUsed,
    exceedsMaximum,
    belowMinimum,
    finalCoverageAmount,
    requiresEOI,
    guaranteedIssueAmount,
    amountRequiringEOI,
  };
}

/**
 * Calculate monthly premium based on coverage amount and rate per unit
 */
export function calculateMonthlyPremium(
  coverageAmount: number,
  ratePerUnit: number,
  unitBasis: number = 1000
): number {
  const units = coverageAmount / unitBasis;
  return units * ratePerUnit;
}

/**
 * Calculate annual premium from monthly premium
 */
export function calculateAnnualPremium(monthlyPremium: number): number {
  return monthlyPremium * 12;
}

/**
 * Validate beneficiary percentages total 100% for each type
 */
export interface BeneficiaryPercentageValidation {
  isValid: boolean;
  primaryTotal: number;
  contingentTotal: number;
  errors: string[];
}

export function validateBeneficiaryPercentages(
  beneficiaries: Array<{ beneficiaryType: string; percentage: number }>
): BeneficiaryPercentageValidation {
  const primaryTotal = beneficiaries
    .filter((b) => b.beneficiaryType === "primary")
    .reduce((sum, b) => sum + b.percentage, 0);

  const contingentTotal = beneficiaries
    .filter((b) => b.beneficiaryType === "contingent")
    .reduce((sum, b) => sum + b.percentage, 0);

  const errors: string[] = [];

  // Primary beneficiaries must total 100% if any exist
  const hasPrimary = beneficiaries.some((b) => b.beneficiaryType === "primary");
  if (hasPrimary && Math.abs(primaryTotal - 100) > 0.01) {
    errors.push(
      `Primary beneficiary percentages must total 100% (current: ${primaryTotal}%)`
    );
  }

  // Contingent beneficiaries must total 100% if any exist
  const hasContingent = beneficiaries.some((b) => b.beneficiaryType === "contingent");
  if (hasContingent && Math.abs(contingentTotal - 100) > 0.01) {
    errors.push(
      `Contingent beneficiary percentages must total 100% (current: ${contingentTotal}%)`
    );
  }

  return {
    isValid: errors.length === 0,
    primaryTotal,
    contingentTotal,
    errors,
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format coverage amount (typically whole dollars, no decimals)
 */
export function formatCoverageAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
